"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { getQueryClient } from "@/app/get-query-client";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { datadogRum } from "@datadog/browser-rum";
import config from "@/configuration";
import ClerkTokenProvider from "@/components/ClerkTokenProvider";

// Module-level so RUM is up before the first render, but browser-only: this
// "use client" module is also evaluated during SSR on the Worker, where the
// SDK has no storage and logs "No storage available for session" on every
// request (visible in Datadog as a warn per page view since the OpenNext move).
if (typeof window !== "undefined") {
  datadogRum.init({
    applicationId: "84fe808d-c5ce-46a0-b1c3-494c70486554",
    clientToken: "pube680a538bdb60e4ce5b772b6f9780458",
    site: "ap1.datadoghq.com",
    service: "shipmate-dashboard",
    env: config.environment,

    // Specify a version number to identify the deployed version of your application in Datadog
    version: config.version,
    sessionSampleRate: config.environment === "prod" ? 100 : 0,
    sessionReplaySampleRate: config.environment === "prod" ? 25 : 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    // Specify URLs to propagate trace headers for connection between RUM and backend trace
    allowedTracingUrls: [
      // Same-origin routes (e.g. /api/* route handlers)
      { match: /^\/api(\/|$)/, propagatorTypes: ["tracecontext", "datadog"] },

      // Production API (direct calls from browser)
      {
        match: "https://api.shipmate.jp/",
        propagatorTypes: ["tracecontext", "datadog"],
      },

      // Local dev API (direct calls from browser)
      {
        match: /^http:\/\/localhost:\d+(\/|$)/,
        propagatorTypes: ["tracecontext", "datadog"],
      },
      {
        match: /^http:\/\/127\.0\.0\.1:\d+(\/|$)/,
        propagatorTypes: ["tracecontext", "datadog"],
      },
    ],
    plugins: [],
  });
}

const queryClient = getQueryClient();

export default function Providers({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ClerkTokenProvider />
      <PHProvider client={posthog}>{children}</PHProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}
